import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["grid", "monthYear", "modal"]
  static values = {
    eventsUrl: String
  }

  connect() {
    this.currentDate = new Date()
    this.events = []
    this.renderCalendar()
    this.loadEvents()
  }

  today() {
    this.currentDate = new Date()
    this.renderCalendar()
  }

  prevMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() - 1)
    this.renderCalendar()
    this.loadEvents()
  }

  nextMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() + 1)
    this.renderCalendar()
    this.loadEvents()
  }

  renderCalendar() {
    const year = this.currentDate.getFullYear()
    const month = this.currentDate.getMonth()

    // Update month/year display
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"]
    this.monthYearTarget.textContent = `${monthNames[month]} ${year}`

    // Get first day of month and total days
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDayOfWeek = firstDay.getDay()
    const totalDays = lastDay.getDate()

    // Get today for highlighting
    const today = new Date()
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year

    // Build calendar grid
    let html = ''

    // Empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      html += '<div class="min-h-[80px] border-r border-b border-[#E8E4DF] last:border-r-0 bg-[#FAF8F5]"></div>'
    }

    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const isToday = isCurrentMonth && today.getDate() === day
      const dayEvents = this.getEventsForDate(dateStr)

      html += `
        <div class="min-h-[80px] border-r border-b border-[#E8E4DF] last:border-r-0 p-1 ${isToday ? 'bg-[#C4A484]/5' : ''}"
             data-date="${dateStr}">
          <div class="text-xs font-medium ${isToday ? 'bg-[#C4A484] text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-[#8C8C8C]'}">
            ${day}
          </div>
          <div class="mt-1 space-y-1">
            ${dayEvents.map(event => `
              <div class="text-xs p-1 rounded bg-[#C4A484]/10 text-[#2D2D2D] truncate cursor-pointer hover:bg-[#C4A484]/20"
                   title="${this.escapeHtml(event.title)}"
                   data-event-id="${event.id}"
                   data-action="click->google-calendar#showEventDetail">
                ${this.escapeHtml(event.title)}
              </div>
            `).join('')}
          </div>
        </div>
      `
    }

    // Fill remaining cells
    const totalCells = startDayOfWeek + totalDays
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7)
    for (let i = 0; i < remainingCells; i++) {
      html += '<div class="min-h-[80px] border-r border-b border-[#E8E4DF] last:border-r-0 bg-[#FAF8F5]"></div>'
    }

    this.gridTarget.innerHTML = html
  }

  async loadEvents() {
    if (!this.eventsUrlValue) return

    const year = this.currentDate.getFullYear()
    const month = this.currentDate.getMonth()
    const start = new Date(year, month, 1).toISOString()
    const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString()

    try {
      const response = await fetch(`${this.eventsUrlValue}?start=${start}&end=${end}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        this.events = await response.json()
        this.renderCalendar()
      } else if (response.status === 401) {
        // Credentials expired, reload to show connect button
        window.location.reload()
      }
    } catch (error) {
      console.error("Failed to load calendar events:", error)
    }
  }

  getEventsForDate(dateStr) {
    return this.events.filter(event => {
      const eventDate = event.start?.split('T')[0] || event.start
      return eventDate === dateStr
    })
  }

  showEventModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      // Set default start/end times
      const now = new Date()
      now.setMinutes(0)
      now.setSeconds(0)
      const later = new Date(now.getTime() + 60 * 60 * 1000)

      const startInput = this.modalTarget.querySelector('[name="event[start_time]"]')
      const endInput = this.modalTarget.querySelector('[name="event[end_time]"]')

      if (startInput) startInput.value = this.formatDateTimeLocal(now)
      if (endInput) endInput.value = this.formatDateTimeLocal(later)
    }
  }

  hideEventModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
    }
  }

  async createEvent(event) {
    event.preventDefault()
    const form = event.target

    try {
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        this.hideEventModal()
        form.reset()
        await this.loadEvents()
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to create event')
      }
    } catch (error) {
      console.error("Failed to create event:", error)
      alert('Failed to create event')
    }
  }

  showEventDetail(event) {
    const eventId = event.currentTarget.dataset.eventId
    const calendarEvent = this.events.find(e => e.id === eventId)
    if (calendarEvent) {
      alert(`${calendarEvent.title}\n\n${calendarEvent.description || 'No description'}`)
    }
  }

  formatDateTimeLocal(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
